
/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db as firestore } from '@/lib/firebase';
import i18n from '@/i18n';

interface AppConfig {
  companyName: string;
  companyLogo: string;
  primaryColor: string;
  secondaryColor: string;
  currency: string;
  dateFormat: string;
  timeFormat: string;
  crInvoicingEnabled: boolean;
  defaultCabysCode: string;
  language: string;
  darkMode: boolean;
  timezone: string;
  country: string;
}

interface AppConfigContextType {
  config: AppConfig;
  updateConfig: (newConfig: Partial<AppConfig>) => Promise<void>;
  updateDefaultCurrency: (currency: string) => Promise<void>;
  updateAppLogo: (logoUrl: string | null) => Promise<void>;
  toggleCrInvoicing: (enabled: boolean) => Promise<void>;
  loading: boolean;
  logoUrl: string;
  defaultCurrency: string;
  crInvoicingEnabled: boolean;
  defaultCabysCode: string;
  language: string;
  darkMode: boolean;
  toggleDarkMode: () => Promise<void>;
}

const defaultConfig: AppConfig = {
  companyName: 'Bassline CRM',
  companyLogo: '',
  primaryColor: '#3B82F6',
  secondaryColor: '#10B981',
  currency: 'USD',
  dateFormat: 'MM/dd/yyyy',
  timeFormat: '12h',
  crInvoicingEnabled: false,
  defaultCabysCode: '9619900000000',
  language: '',
  darkMode: false,
  timezone: 'UTC',
  country: 'CR',
};

const AppConfigContext = createContext<AppConfigContextType | undefined>(undefined);

export const useAppConfig = () => {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
};

interface AppConfigProviderProps {
  children: ReactNode;
}

export const AppConfigProvider: React.FC<AppConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<AppConfig>(defaultConfig);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      
      // 1. Determine initial language (only from sources of truth)
      const detectedLng = i18n.language?.split('-')[0];
      const storedLng = localStorage.getItem('i18nextLng');
      const initialLng = (storedLng === 'es' || storedLng === 'en') ? storedLng : 
                         (detectedLng === 'es' || detectedLng === 'en') ? detectedLng : '';
      
      try {
        const settingsRef = doc(firestore, 'settings', 'appSettings');
        const snap = await getDoc(settingsRef);
        
        if (snap.exists()) {
          const data = snap.data();
          const newConfig = {
            ...defaultConfig,
            companyName: 'Bassline', // Hardcoded as requested
            companyLogo: data.logoUrl || defaultConfig.companyLogo,
            currency: data.currency || defaultConfig.currency,
            crInvoicingEnabled: data.crInvoicingEnabled ?? defaultConfig.crInvoicingEnabled,
            defaultCabysCode: data.defaultCabysCode || defaultConfig.defaultCabysCode,
            language: data.language || storedLng || '',
            darkMode: data.darkMode ?? defaultConfig.darkMode,
            timezone: data.timezone || defaultConfig.timezone,
            country: data.country || defaultConfig.country,
          };
          
          setConfig(newConfig);
          
          // Sync i18n if we have a preference
          if (newConfig.language && newConfig.language !== i18n.language) {
            i18n.changeLanguage(newConfig.language);
          } else if (!newConfig.language && initialLng) {
            // If no preference but we detected a language, use it for i18n but keep config.language empty
            i18n.changeLanguage(initialLng);
          }
        } else {
          // No Firestore settings yet, allow LanguageGate to trigger by keeping language empty
          // But set i18n to detected language for the gate UI itself
          setConfig(prev => ({ ...prev, language: storedLng || '' }));
          if (storedLng || initialLng) {
            i18n.changeLanguage(storedLng || initialLng);
          }
        }
      } catch (err) {
        console.error('Failed to load global config:', err);
        // Fallback to detected language for UI only
        if (initialLng) {
          i18n.changeLanguage(initialLng);
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadSettings();
  }, [user?.uid]);

  useEffect(() => {
    if (config.darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Save locally if needed
    localStorage.setItem('theme', config.darkMode ? 'dark' : 'light');
  }, [config.darkMode]);

  const updateConfig = async (newConfig: Partial<AppConfig>) => {
    try {
      const updatedConfig = { ...config, ...newConfig };
      // Update state first for immediate UI response locally
      setConfig(updatedConfig);
      
      // If user is not authenticated, we only update local state (so UI like LanguageGate can dismiss)
      if (!user?.uid) return;
      
      const settingsRef = doc(firestore, 'settings', 'appSettings');
      await setDoc(settingsRef, updatedConfig, { merge: true });
    } catch (error) {
      console.warn('Could not sync appConfig to Firestore:', error);
      // We keep the local state updated for a smooth offline / permission-safe UI experience
    }
  };

  const updateDefaultCurrency = async (currency: string) => {
    await updateConfig({ currency });
  };

  const updateAppLogo = async (logoUrl: string | null) => {
    await updateConfig({ companyLogo: logoUrl || '' });
  };

  const toggleCrInvoicing = async (enabled: boolean) => {
    await updateConfig({ crInvoicingEnabled: enabled });
  };

  const toggleDarkMode = async () => {
    await updateConfig({ darkMode: !config.darkMode });
  };

  const value = {
    config,
    updateConfig,
    updateDefaultCurrency,
    updateAppLogo,
    toggleCrInvoicing,
    loading,
    logoUrl: config.companyLogo,
    defaultCurrency: config.currency,
    crInvoicingEnabled: config.crInvoicingEnabled,
    defaultCabysCode: config.defaultCabysCode,
    language: config.language,
    darkMode: config.darkMode,
    toggleDarkMode,
  };

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
};
